import { useEffect, useRef, useState } from 'react'
import {
  ApiError, saveSiteLayout, siteLayout,
  type Pen, type PenShape, type SiteLayout, type Structure,
} from '../api'
import {
  DEFAULT_BARGE_M, DEFAULT_CIRCUMFERENCE_M,
  centroid, offset, penFeature, radiusM, rotateAbout, seedPositions,
  sideM, spreadAbout, structureFeature, toLocal, turn,
  type Point,
} from '../merdgeo'
import { useT } from '../lang'

/* Drawing the farm.
 *
 * WHY A MAP AND NOT A FORM WITH LATITUDE AND LONGITUDE BOXES.
 *
 * Nobody knows where merd 3 is. They know it is the one at the mouth, second
 * from the flåte, and they know it the moment they see the picture. Positions
 * on the water are read off a photograph and never dictated — which is exactly
 * what scripts/mkpens.sh cannot do, and why the same act that names the rings
 * now also places them.
 *
 * WHAT IT IS FOR, which is not admin tidiness.
 *
 * "Merd 3 is always the worst" is a sentence about the mouth of the fjord and
 * the way the current sets, and no ordering of a table can say it. Eight
 * polygons on a satellite picture say it in a glance. The whole customer-facing
 * lice product is going to be this picture with the rings coloured by what was
 * counted in them; this screen is where the picture comes from, and it is staff
 * side because the ring set is what every count and every statutory report is
 * addressed against.
 *
 * THE ARRAY IS THE UNIT OF EDITING, NOT THE RING.
 *
 * The first version let you drag one ring at a time, and the first person to
 * use it laid out eight and found all eight in the wrong place. That is the
 * ordinary case rather than a mishap: an automatic layout gets the
 * RELATIONSHIPS right — the spacing, the two rows, the order — and the
 * position, the orientation and the scale wrong. Dragging rings one at a time
 * destroys the part that was right in order to fix the part that was not. So
 * the array has a handle of its own, a rotation and a spacing, and those three
 * controls are the whole of the fix.
 *
 * THE DRAG TARGET IS A MARKER, NOT THE POLYGON.
 *
 * Hit-testing a fill layer means tracking pointer events against a WebGL canvas
 * and disabling pan mid-gesture, and it is fragile on touch — which is the
 * device this will eventually be used on. A MapLibre marker is already
 * draggable, already works with a finger, and gives the ring its number as
 * ordinary HTML that can be styled and read by a screen reader. The polygon
 * underneath is pure decoration and takes no events at all.
 *
 * Ctrl-drag is left to the map, which uses it to rotate and tilt the camera.
 * Turning the VIEW to line up with the fjord and turning the ARRAY are
 * different acts and both are wanted, so neither gets to shadow the other.
 */

const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined

/* Hardcoded rather than read from the tokens in styles.css, because MapLibre
   paint expressions run in WebGL and cannot see a CSS variable. Kept in step
   by hand and named after the token they mirror. */
const C_PEN = '#b4d9c7'        // --signal
const C_SELECTED = '#e63926'   // --pulse
const C_STRUCTURE = '#e6f1ea'  // --text

/* The basemaps, and what is actually available.
 *
 * Mapbox satellite is the default because it is the only one of the three that
 * shows the rings themselves, and tracing a ring off the picture is how sizes
 * get set here.
 *
 * The two Kartverket layers come from cache.kartverket.no, which is open, needs
 * no key and is the current service — opencache.statkart.no is dead. The tile
 * template is {z}/{y}/{x}: WMTS numbers TileRow before TileCol, and swapping
 * them gives a map of somewhere else in Norway rather than an error.
 *
 * NORGE I BILDER IS NOT HERE, and not for want of trying: wms.geonorge.no's
 * `wms.nib` answers "Bruker kan ikke autentiseres" to an anonymous request. It
 * sits behind a Geonorge agreement whose ticket is issued per registered IP —
 * which a browser cannot hold, and which the service could not parse from this
 * host anyway ("Bad TCP/IP address" for an IPv6 caller). Reaching it means an
 * agreement and a proxy through the receiver, which is a decision rather than a
 * line of code.
 */
const BASEMAPS = [
  { id: 'sat', label: 'Satellite',
    tiles: (tok: string) =>
      `https://api.tiles.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}.jpg?access_token=${tok}`,
    attribution: '© Mapbox · © OpenStreetMap' },
  { id: 'sjo', label: 'Nautical chart',
    tiles: () =>
      'https://cache.kartverket.no/v1/wmts/1.0.0/sjokartraster/default/webmercator/{z}/{y}/{x}.png',
    attribution: '© Kartverket' },
  { id: 'topo', label: 'Map',
    tiles: () =>
      'https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/{z}/{y}/{x}.png',
    attribution: '© Kartverket' },
] as const

type BaseId = (typeof BASEMAPS)[number]['id']

type Key = string

/** The array's own handle, and the ring-resize handle. Reserved keys, so the
 *  marker reconciler can hold all three kinds in one map without a pen ever
 *  colliding with one — pen keys are `k1`, `k2`, … */
const GROUP = '__group'
const SIZER = '__sizer'

interface PenDraft {
  key: Key
  pen_id?: string
  pen_no: string
  name: string | null
  circumference_m: number | null
  depth_m: number | null
  lat: number | null
  lon: number | null
  shape: PenShape
  bearing_deg: number | null
}

interface StructDraft {
  key: Key
  structure_id?: string
  kind: Structure['kind']
  name: string | null
  lat: number
  lon: number
  length_m: number | null
  width_m: number | null
  bearing_deg: number | null
}

let seq = 0
const nextKey = () => `k${++seq}`

function toPen(d: PenDraft): Pen {
  return {
    pen_id: d.pen_id ?? d.key, pen_no: d.pen_no, name: d.name,
    circumference_m: d.circumference_m, depth_m: d.depth_m, sort: 0,
    lat: d.lat, lon: d.lon, shape: d.shape, bearing_deg: d.bearing_deg,
  }
}

function toStruct(d: StructDraft): Structure {
  return {
    structure_id: d.structure_id ?? d.key, kind: d.kind, name: d.name,
    lat: d.lat, lon: d.lon, length_m: d.length_m, width_m: d.width_m,
    bearing_deg: d.bearing_deg,
  }
}

/** The next free merd number, as a farmer would pick it.
 *
 *  Counting up from 1 and taking the first gap rather than max+1: a farm that
 *  retired merd 2 and is drawing its replacement wants 2 back, and that is
 *  what the partial unique index on the receiver is for. */
function nextPenNo(pens: PenDraft[]): string {
  const taken = new Set(pens.map((p) => p.pen_no.trim()))
  for (let i = 1; i < 200; i++) if (!taken.has(String(i))) return String(i)
  return String(pens.length + 1)
}

/** Where the ring-resize handle sits: on the ring's edge, due east of its
 *  centre, and on a square turned with the square so it stays on an edge
 *  rather than wandering off a corner. */
function sizerAt(p: PenDraft): Point | null {
  if (p.lat === null || p.lon === null) return null
  const d = p.shape === 'square'
    ? sideM(p.circumference_m) / 2 : radiusM(p.circumference_m)
  const [e, n] = turn(d, 0, -(p.bearing_deg ?? 0))
  const [lon, lat] = offset(p.lat, p.lon, e, n)
  return { lat, lon }
}

/** …and back again: what circumference does a handle this far out mean?
 *
 *  Round: the handle is on the circle, so C = 2πr. Square: the handle is at
 *  the middle of an edge, so that distance is half a side and C = 8d. Clamped,
 *  so a handle dropped on the ring's own centre does not leave a ring of zero
 *  metres that can never be picked up again. */
function circumferenceFrom(p: PenDraft, d: number): number {
  const c = p.shape === 'square' ? d * 8 : d * 2 * Math.PI
  return Math.round(Math.max(20, Math.min(1000, c)))
}

export function Anleggskart({ siteId }: { siteId: string }) {
  const t = useT()
  const el = useRef<HTMLDivElement>(null)
  const map = useRef<any>(null)
  const lib = useRef<any>(null)
  const marks = useRef<Map<Key, any>>(new Map())
  const [ready, setReady] = useState(0)
  const [failed, setFailed] = useState<string | null>(null)

  const [loaded, setLoaded] = useState<SiteLayout | null>(null)
  const [pens, setPens] = useState<PenDraft[]>([])
  const [structs, setStructs] = useState<StructDraft[]>([])
  const [sel, setSel] = useState<Key | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [base, setBase] = useState<BaseId>('sat')
  /* Two dials rather than two absolute values. They say how far the array has
     been turned and spread SINCE THIS SCREEN OPENED, because there is no
     stored orientation to be absolute about — a farm's rings are eight
     positions, and "the array's bearing" is something a reader infers from
     them rather than a field. Relative also means the dials compose: turn,
     spread, turn again, and each acts on what is there. */
  const [rot, setRot] = useState(0)
  const [spread, setSpread] = useState(100)
  /* What the next click on the map will drop, if anything.
     Armed by clicking a palette entry — the touch-friendly half of the same
     gesture the palette also offers as an HTML5 drag. */
  const [arm, setArm] = useState<null | 'circle' | 'square' | 'barge'>(null)

  /* ------------------------------------------------------------- loading */

  const adopt = (r: SiteLayout) => {
    setLoaded(r)
    setPens(r.pens.map((p) => ({
      key: nextKey(), pen_id: p.pen_id, pen_no: p.pen_no, name: p.name,
      circumference_m: p.circumference_m, depth_m: p.depth_m,
      lat: p.lat ?? null, lon: p.lon ?? null,
      shape: p.shape ?? 'circle', bearing_deg: p.bearing_deg ?? null,
    })))
    setStructs(r.structures.map((s) => ({ key: nextKey(), ...s })))
  }

  useEffect(() => {
    let dead = false
    void siteLayout(siteId).then((r) => { if (!dead) adopt(r) }).catch((e) => {
      if (!dead) setErr(e instanceof ApiError ? (e.detail ?? e.code)
        : t('Could not reach the receiver.'))
    })
    return () => { dead = true }
  }, [siteId])

  /* Latest state, for the map callbacks. The map is built once and its
     handlers must not close over the first render's arrays. */
  const now = useRef({ pens, structs, sel, arm })
  now.current = { pens, structs, sel, arm }

  const touch = () => { setDirty(true); setSaved(false) }

  const placed: Point[] = [
    ...pens.filter((p) => p.lat !== null && p.lon !== null)
      .map((p) => ({ lat: p.lat!, lon: p.lon! })),
    ...structs.map((s) => ({ lat: s.lat, lon: s.lon })),
  ]
  const middle = centroid(placed)

  /* --------------------------------------------------------------- adding */

  const place = (what: 'circle' | 'square' | 'barge',
                 lat: number, lon: number) => {
    touch()
    if (what === 'barge') {
      const key = nextKey()
      setStructs((xs) => [...xs, {
        key, kind: 'barge', name: null, lat, lon,
        length_m: DEFAULT_BARGE_M.length, width_m: DEFAULT_BARGE_M.width,
        bearing_deg: 0,
      }])
      setSel(key)
      return
    }
    const key = nextKey()
    setPens((ps) => [...ps, {
      key, pen_no: nextPenNo(ps), name: null,
      // Inherited from the ring most recently placed, because a farm's rings
      // are all the same size and typing 160 eight times is the kind of work
      // that makes somebody stop halfway.
      circumference_m: ps[ps.length - 1]?.circumference_m
        ?? DEFAULT_CIRCUMFERENCE_M,
      depth_m: ps[ps.length - 1]?.depth_m ?? null,
      lat, lon, shape: what,
      bearing_deg: what === 'square'
        ? (ps.find((p) => p.shape === 'square')?.bearing_deg ?? 0) : null,
    }])
    setSel(key)
  }

  /* ---------------------------------------------------------- the drawing */

  interface Live {
    /** Move things while a finger is still down, without a re-render. */
    at?: (key: Key, lat: number, lon: number) => Point
    /** Resize one ring the same way. */
    size?: { key: Key; circumference_m: number }
  }

  const paint = (live?: Live) => {
    const m = map.current
    const src = m?.getSource?.('layout')
    if (!src) return
    const { pens, structs, sel } = now.current
    const move = <T extends { key: Key; lat: number | null;
                              lon: number | null }>(o: T): T => {
      if (!live?.at || o.lat === null || o.lon === null) return o
      const p = live.at(o.key, o.lat, o.lon)
      return { ...o, lat: p.lat, lon: p.lon }
    }
    const resize = (p: PenDraft): PenDraft =>
      (live?.size && live.size.key === p.key
        ? { ...p, circumference_m: live.size.circumference_m } : p)

    // Structures first so a ring never disappears behind the flåte. Within one
    // layer MapLibre draws in source order.
    const features = [
      ...structs.map(move).map((s) =>
        structureFeature(toStruct(s), { selected: sel === s.key })),
      ...pens.map(move).map(resize).map((p) =>
        penFeature(toPen(p), { selected: sel === p.key })),
    ].filter(Boolean)
    src.setData({ type: 'FeatureCollection', features })
  }

  /* ---------------------------------------------------- moving the array */

  const moveAll = (dLat: number, dLon: number) => {
    touch()
    setPens((ps) => ps.map((p) => (p.lat === null ? p
      : { ...p, lat: p.lat + dLat, lon: p.lon! + dLon })))
    setStructs((xs) => xs.map((s) =>
      ({ ...s, lat: s.lat + dLat, lon: s.lon + dLon })))
  }

  const turnAll = (deg: number) => {
    const c = middle
    if (!c || !deg) return
    touch()
    setPens((ps) => ps.map((p) => {
      if (p.lat === null || p.lon === null) return p
      const q = rotateAbout(c, { lat: p.lat, lon: p.lon }, deg)
      return {
        ...p, ...q,
        // A square ring turns WITH the array. Rotating only the positions
        // leaves eight cages sitting askew inside their own layout, which is
        // wrong in the picture and would be wrong in the water.
        bearing_deg: p.shape === 'square'
          ? ((((p.bearing_deg ?? 0) + deg) % 360) + 360) % 360 : null,
      }
    }))
    setStructs((xs) => xs.map((s) => ({
      ...s, ...rotateAbout(c, { lat: s.lat, lon: s.lon }, deg),
      bearing_deg: ((((s.bearing_deg ?? 0) + deg) % 360) + 360) % 360,
    })))
  }

  const spreadAll = (factor: number) => {
    const c = middle
    if (!c || factor === 1) return
    touch()
    // Spacing, not size: the gaps change and the rings keep their diameter,
    // because a ring's size is a fact about the ring and the gap is a fact
    // about the mooring.
    setPens((ps) => ps.map((p) => (p.lat === null || p.lon === null ? p
      : { ...p, ...spreadAbout(c, { lat: p.lat, lon: p.lon }, factor) })))
    setStructs((xs) => xs.map((s) =>
      ({ ...s, ...spreadAbout(c, { lat: s.lat, lon: s.lon }, factor) })))
  }

  /* ------------------------------------------------------------ markers */

  /* One per placed object, plus the array's handle and the resize handle,
     reconciled against the drafts. Rebuilt only when the set of keys changes;
     position, label and selection are updated in place, so a drag does not tear
     down the element under the hand doing the dragging. */
  useEffect(() => {
    const m = map.current, maplibregl = lib.current
    if (!m || !maplibregl) return

    const selPen = pens.find((p) => p.key === sel) ?? null
    const sizer = selPen ? sizerAt(selPen) : null

    interface Want { lat: number; lon: number; label: string; cls: string[] }
    const want = new Map<Key, Want>()
    for (const p of pens) {
      if (p.lat === null || p.lon === null) continue
      want.set(p.key, { lat: p.lat, lon: p.lon, label: p.pen_no,
                        cls: sel === p.key ? ['on'] : [] })
    }
    for (const s of structs) {
      want.set(s.key, { lat: s.lat, lon: s.lon, label: '▭',
                        cls: sel === s.key ? ['flate', 'on'] : ['flate'] })
    }
    // The array's handle sits at the centroid of everything placed, which is
    // where a hand reaches when the thought is "move the farm".
    if (middle && placed.length > 1) {
      want.set(GROUP, { ...middle, label: '✛', cls: ['gruppe'] })
    }
    if (sizer) want.set(SIZER, { ...sizer, label: '', cls: ['sizer'] })

    for (const [key, mk] of marks.current) {
      if (!want.has(key)) { mk.remove(); marks.current.delete(key) }
    }

    for (const [key, w] of want) {
      let mk = marks.current.get(key)
      if (!mk) {
        const node = document.createElement('button')
        node.type = 'button'
        node.classList.add('merdmark')
        node.addEventListener('click', (e) => {
          e.stopPropagation()
          // Neither handle is a thing you can select; they act on what is
          // already selected, or on everything.
          if (key !== GROUP && key !== SIZER) setSel(key)
        })
        mk = new maplibregl.Marker({ element: node, draggable: true })
        mk.setLngLat([w.lon, w.lat]).addTo(m)

        if (key === GROUP) {
          // Delta from where the handle started, applied to everything. Read
          // off the handle rather than accumulated, so a drag that wanders and
          // comes back leaves the array where it began.
          let from: Point | null = null
          mk.on('dragstart', () => {
            const p = mk.getLngLat()
            from = { lat: p.lat, lon: p.lng }
          })
          mk.on('drag', () => {
            if (!from) return
            const p = mk.getLngLat()
            const dLat = p.lat - from.lat, dLon = p.lng - from.lon
            paint({ at: (_k, la, lo) => ({ lat: la + dLat, lon: lo + dLon }) })
            // The numbers travel with their rings. Without this the polygons
            // slide under a set of markers that stay put and then snap at the
            // end, which reads as the drawing coming apart.
            const { pens, structs } = now.current
            for (const o of [...pens, ...structs]) {
              const other = marks.current.get(o.key)
              if (!other || o.lat === null || o.lon === null) continue
              other.setLngLat([o.lon + dLon, o.lat + dLat])
            }
          })
          mk.on('dragend', () => {
            if (!from) return
            const p = mk.getLngLat()
            moveAll(p.lat - from.lat, p.lng - from.lon)
            from = null
          })
        } else if (key === SIZER) {
          const measure = () => {
            const { pens, sel } = now.current
            const p = pens.find((x) => x.key === sel)
            if (!p || p.lat === null || p.lon === null) return null
            const h = mk.getLngLat()
            const [e, n] = toLocal({ lat: p.lat, lon: p.lon },
                                   { lat: h.lat, lon: h.lng })
            return { key: p.key, c: circumferenceFrom(p, Math.hypot(e, n)) }
          }
          mk.on('drag', () => {
            const r = measure()
            if (r) paint({ size: { key: r.key, circumference_m: r.c } })
          })
          mk.on('dragend', () => {
            const r = measure()
            if (!r) return
            touch()
            setPens((ps) => ps.map((x) =>
              (x.key === r.key ? { ...x, circumference_m: r.c } : x)))
          })
        } else {
          mk.on('dragstart', () => setSel(key))
          // Repaint from the marker rather than from state: at 60 frames a
          // second a setState per frame would re-render the side panel too,
          // and the polygon is the only thing that has to keep up.
          mk.on('drag', () => {
            const p = mk.getLngLat()
            paint({ at: (k, la, lo) =>
              (k === key ? { lat: p.lat, lon: p.lng } : { lat: la, lon: lo }) })
          })
          mk.on('dragend', () => {
            const p = mk.getLngLat()
            touch()
            setPens((ps) => ps.map((x) =>
              (x.key === key ? { ...x, lat: p.lat, lon: p.lng } : x)))
            setStructs((xs) => xs.map((x) =>
              (x.key === key ? { ...x, lat: p.lat, lon: p.lng } : x)))
          })
        }
        marks.current.set(key, mk)
      }
      mk.setLngLat([w.lon, w.lat])
      const node = mk.getElement() as HTMLElement
      // classList, NOT `className =`. MapLibre puts its own `maplibregl-marker`
      // class on the element we hand it, and that class is what carries
      // `position: absolute` — assigning className wholesale strips it, and
      // every marker falls out of the map into normal flow.
      for (const c of ['on', 'flate', 'gruppe', 'sizer']) {
        node.classList.toggle(c, w.cls.includes(c))
      }
      if (node.textContent !== w.label) node.textContent = w.label
    }
    paint()
  }, [pens, structs, sel, ready])

  /* ------------------------------------------------------------- the map */

  useEffect(() => {
    if (!TOKEN) { setFailed('no-token'); return }
    let dead = false
    let m: any
    void (async () => {
      try {
        const maplibregl = (await import('maplibre-gl')).default
        await import('maplibre-gl/dist/maplibre-gl.css')
        if (dead || !el.current) return
        lib.current = maplibregl

        /* All three basemaps in the style from the start, switched by
           visibility. `setStyle` would tear down the layout source and every
           marker with it, which on a screen whose whole content is unsaved
           work is the wrong trade for a smaller style object. */
        const sources: Record<string, any> = {}
        for (const b of BASEMAPS) {
          sources[b.id] = {
            type: 'raster', tiles: [b.tiles(TOKEN)], tileSize: 256,
            attribution: b.attribution,
          }
        }
        m = new maplibregl.Map({
          container: el.current,
          attributionControl: false,
          style: {
            version: 8,
            sources,
            layers: BASEMAPS.map((b) => ({
              id: `base-${b.id}`, type: 'raster', source: b.id,
              layout: { visibility: b.id === 'sat' ? 'visible' : 'none' },
            })),
          },
          center: [4.9, 60.5],
          zoom: 5,
        })
        map.current = m
        // With the compass, because ctrl-drag turns the camera and a map that
        // is no longer north-up with nothing saying so is a map somebody reads
        // a bearing off wrongly. Clicking it puts north back.
        m.addControl(new maplibregl.NavigationControl({ showCompass: true }),
                     'top-right')
        m.addControl(new maplibregl.AttributionControl({ compact: true }),
                     'bottom-right')

        m.on('load', () => {
          m.addSource('layout',
                      { type: 'geojson',
                        data: { type: 'FeatureCollection', features: [] } })
          m.addLayer({
            id: 'layout-fill', type: 'fill', source: 'layout',
            paint: {
              'fill-color': ['case',
                ['get', 'selected'], C_SELECTED,
                ['==', ['get', 'kind'], 'structure'], C_STRUCTURE,
                C_PEN],
              'fill-opacity': 0.28,
            },
          })
          // Two line layers rather than one, because line-dasharray is not
          // data-driven. A ring whose size nobody has told us is drawn dashed:
          // a missing measurement shown as a guess, rather than a guess shown
          // as a fact.
          for (const [id, filter, dash] of [
            ['layout-line', ['!', ['to-boolean', ['get', 'assumed']]], null],
            ['layout-line-assumed', ['to-boolean', ['get', 'assumed']],
             [2, 2]],
          ] as [string, any, number[] | null][]) {
            m.addLayer({
              id, type: 'line', source: 'layout', filter,
              paint: {
                'line-color': ['case',
                  ['get', 'selected'], C_SELECTED,
                  ['==', ['get', 'kind'], 'structure'], C_STRUCTURE,
                  C_PEN],
                'line-width': ['case', ['get', 'selected'], 3, 1.8],
                ...(dash ? { 'line-dasharray': dash } : {}),
              },
            })
          }
          setReady((n) => n + 1)
        })

        m.on('click', (e: any) => {
          const a = now.current.arm
          if (a) { place(a, e.lngLat.lat, e.lngLat.lng); setArm(null) }
          else setSel(null)
        })
        m.on('error', (e: any) => {
          if (e?.error?.status === 401 || e?.error?.status === 403)
            setFailed('token')
        })
      } catch {
        if (!dead) setFailed('load')
      }
    })()
    return () => {
      dead = true
      marks.current.clear()
      try { m?.remove() } catch { /* already gone */ }
    }
  }, [])

  useEffect(() => {
    const m = map.current
    if (!m || !ready) return
    for (const b of BASEMAPS) {
      m.setLayoutProperty(`base-${b.id}`, 'visibility',
                          b.id === base ? 'visible' : 'none')
    }
  }, [base, ready])

  /* Frame the farm once it is known. Only the first time — a reload of the
     drafts must not yank the view out from under somebody who has just panned
     to the right bay. */
  const framed = useRef(false)
  useEffect(() => {
    const m = map.current, maplibregl = lib.current
    if (!m || !maplibregl || !loaded || framed.current) return
    const pts: [number, number][] = [
      ...loaded.pens.filter((p) => p.lat != null)
        .map((p) => [p.lon as number, p.lat as number] as [number, number]),
      ...loaded.structures.map((s) => [s.lon, s.lat] as [number, number]),
    ]
    if (pts.length === 0 && loaded.site.lat != null) {
      pts.push([loaded.site.lon as number, loaded.site.lat as number])
    }
    if (pts.length === 0) return
    framed.current = true
    if (pts.length === 1) {
      m.jumpTo({ center: pts[0], zoom: 16 })
    } else {
      const b = pts.reduce((acc, p) => acc.extend(p),
                           new maplibregl.LngLatBounds(pts[0], pts[0]))
      m.fitBounds(b, { padding: 80, maxZoom: 17, duration: 0 })
    }
  }, [loaded, ready])

  /* ------------------------------------------------------------- editing */

  const patch = (key: Key, p: Partial<PenDraft>) => {
    touch()
    setPens((ps) => ps.map((x) => (x.key === key ? { ...x, ...p } : x)))
  }
  const patchS = (key: Key, p: Partial<StructDraft>) => {
    touch()
    setStructs((xs) => xs.map((x) => (x.key === key ? { ...x, ...p } : x)))
  }
  const drop = (key: Key) => {
    touch()
    setPens((ps) => ps.filter((x) => x.key !== key))
    setStructs((xs) => xs.filter((x) => x.key !== key))
    setSel(null)
  }

  const unplaced = pens.filter((p) => p.lat === null)

  /** Seed the rings that have never been placed, in two rows around the farm.
   *
   *  Not saved until somebody presses save. Positions that are roughly right
   *  and obviously draggable beat an empty map: the alternative makes the
   *  first minute of this screen the worst one — and the array handle above
   *  is what turns "roughly right" into right without eight separate drags. */
  const seed = () => {
    /* Around a ring already placed if there is one, otherwise around the
       farm's own recorded position. A farm with neither has nothing to be laid
       out around, and saying so is better than guessing at a centre. */
    const anchored = pens.find((p) => p.lat !== null && p.lon !== null)
    const c = loaded?.site
    const at: Point | null =
      anchored ? { lat: anchored.lat!, lon: anchored.lon! }
        : (c && c.lat !== null && c.lon !== null)
          ? { lat: c.lat, lon: c.lon } : null
    if (!at) {
      setErr(t('This farm has no position yet — drop one pen first.'))
      return
    }
    const seats = seedPositions(unplaced.length, at.lat, at.lon,
                                unplaced[0]?.circumference_m ?? null)
    let i = 0
    touch()
    setPens((ps) => ps.map((p) =>
      (p.lat === null ? { ...p, ...seats[i++]! } : p)))
  }

  const save = async () => {
    setSaving(true); setErr(null)
    try {
      const r = await saveSiteLayout(siteId, {
        pens: pens.map((p, i) => ({
          pen_id: p.pen_id, pen_no: p.pen_no.trim(), name: p.name,
          sort: (i + 1) * 10,
          circumference_m: p.circumference_m, depth_m: p.depth_m,
          lat: p.lat, lon: p.lon, shape: p.shape,
          bearing_deg: p.shape === 'square' ? p.bearing_deg : null,
        })),
        structures: structs.map((s) => ({
          structure_id: s.structure_id, kind: s.kind, name: s.name,
          lat: s.lat, lon: s.lon, length_m: s.length_m, width_m: s.width_m,
          bearing_deg: s.bearing_deg,
        })),
      })
      // Re-keyed from the response, so a ring created in this save carries the
      // id the receiver gave it. Without that the next save would create it a
      // second time and retire the first.
      adopt(r)
      setSel(null); setDirty(false); setSaved(true)
      setRot(0); setSpread(100)
    } catch (e) {
      setErr(e instanceof ApiError ? (e.detail ?? e.code)
        : t('Could not reach the receiver.'))
    } finally {
      setSaving(false)
    }
  }

  /* Leaving with unsaved work. The browser's own prompt rather than a modal of
     our own: it is the one dialog a person already trusts, and a drawing lost
     to a mis-click is twenty minutes. */
  useEffect(() => {
    if (!dirty) return
    const on = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', on)
    return () => window.removeEventListener('beforeunload', on)
  }, [dirty])

  const selPen = pens.find((p) => p.key === sel) ?? null
  const selStruct = structs.find((s) => s.key === sel) ?? null

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const what = e.dataTransfer.getData('text/phoniq') as
      'circle' | 'square' | 'barge'
    const m = map.current
    if (!what || !m || !el.current) return
    const box = el.current.getBoundingClientRect()
    const p = m.unproject([e.clientX - box.left, e.clientY - box.top])
    place(what, p.lat, p.lng)
  }

  if (failed) {
    return (
      <div className="notice bad">
        {failed === 'no-token'
          ? t('The map needs a Mapbox token in this build.')
          : t('The map could not load.')}
      </div>
    )
  }

  return (
    <div className="stack" style={{ gap: 12 }}>
      {loaded && (
        <div className="stack" style={{ gap: 2 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>{loaded.site.name}</h2>
          <div className="faint" style={{ fontSize: 13 }}>
            {[loaded.site.locality_no
              && `${t('Locality')} ${loaded.site.locality_no}`,
              loaded.site.municipality].filter(Boolean).join(' · ')}
          </div>
        </div>
      )}
      {err && <div className="notice bad">{err}</div>}

      <div className="kartrigg">
        <div className="kartboks">
          <div className="kartflate"
            ref={el}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            data-arm={arm ?? undefined} />
          <div className="grunnkart">
            {BASEMAPS.map((b) => (
              <button key={b.id} type="button"
                className={`grunnbit${base === b.id ? ' on' : ''}`}
                onClick={() => setBase(b.id)}>
                {t(b.label)}
              </button>
            ))}
          </div>
        </div>

        <div className="kartrail stack" style={{ gap: 14 }}>
          <div className="stack" style={{ gap: 6 }}>
            <div className="muted" style={{ fontSize: 13 }}>
              {t('Drag onto the map')}
            </div>
            <div className="palett">
              {([['circle', t('Round pen')], ['square', t('Square pen')],
                 ['barge', t('Feed barge')]] as const).map(([k, label]) => (
                <button key={k} type="button" draggable
                  className={`palettbit${arm === k ? ' on' : ''}`}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/phoniq', k)
                    e.dataTransfer.effectAllowed = 'copy'
                  }}
                  onClick={() => setArm(arm === k ? null : k)}>
                  <span className={`glyf ${k}`} aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
            {/* The touch half of the same gesture, said only while it is
                armed. Saying it always would be an instruction manual on a
                screen that does not need one. */}
            {arm && (
              <div className="faint" style={{ fontSize: 12 }}>
                {t('Now tap the map where it goes.')}
              </div>
            )}
          </div>

          {unplaced.length > 0 && (
            <div className="notice" style={{ margin: 0 }}>
              <strong>{unplaced.length}</strong>{' '}
              {t('pens have no position yet.')}{' '}
              <button className="btn sm" onClick={seed}>
                {t('Lay them out')}
              </button>
              <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
                {t('Two rows around the farm, to drag from. Nothing is saved until you save.')}
              </div>
            </div>
          )}

          {/* THE ARRAY, ALWAYS PRESENT once there is more than one thing on the
              water. Not folded away behind a selection: what is wrong after an
              automatic layout is almost always the whole array, and hiding the
              only controls that fix that behind "select nothing first" is how
              somebody ends up dragging eight rings by hand. */}
          {placed.length > 1 && (
            <div className="stack kartvalg" style={{ gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 14 }}>{t('The whole array')}</h3>
              <p className="faint" style={{ margin: 0, fontSize: 12 }}>
                {t('Drag ✛ on the map to move all of it at once.')}
              </p>
              <label className="felt">
                <span>{t('Turn')} {rot > 0 ? `+${rot}` : rot}°</span>
                <input type="range" min={-180} max={180} step={1} value={rot}
                  onChange={(e) => {
                    const v = Number(e.target.value)
                    turnAll(v - rot)
                    setRot(v)
                  }} />
              </label>
              <label className="felt">
                <span>{t('Spacing')} {spread}%</span>
                <input type="range" min={40} max={260} step={2} value={spread}
                  onChange={(e) => {
                    const v = Number(e.target.value)
                    spreadAll(v / spread)
                    setSpread(v)
                  }} />
              </label>
            </div>
          )}

          {selPen && (
            <div className="stack kartvalg" style={{ gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 14 }}>
                {t('Pen')} {selPen.pen_no}
              </h3>
              <label className="felt">
                <span>{t('Pen')}</span>
                <input value={selPen.pen_no} maxLength={16}
                  onChange={(e) => patch(selPen.key, { pen_no: e.target.value })} />
              </label>
              <label className="felt">
                <span>{t('Name')}</span>
                <input value={selPen.name ?? ''} maxLength={200}
                  onChange={(e) => patch(selPen.key,
                                         { name: e.target.value || null })} />
              </label>
              <div className="felt">
                <span>{t('Shape')}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['circle', 'square'] as PenShape[]).map((s) => (
                    <button key={s} type="button"
                      className={`btn sm${selPen.shape === s ? ' primary' : ' ghost'}`}
                      onClick={() => patch(selPen.key, {
                        shape: s,
                        // A circle has no rotation, and the database refuses
                        // one. Cleared here so the two never disagree.
                        bearing_deg: s === 'square'
                          ? (selPen.bearing_deg ?? 0) : null,
                      })}>
                      {s === 'circle' ? t('Round') : t('Square')}
                    </button>
                  ))}
                </div>
              </div>
              <label className="felt">
                <span>{t('Circumference (m)')}</span>
                <input type="number" min={20} max={1000} step={1}
                  value={selPen.circumference_m ?? ''}
                  placeholder={String(DEFAULT_CIRCUMFERENCE_M)}
                  onChange={(e) => patch(selPen.key, {
                    circumference_m: e.target.value
                      ? Number(e.target.value) : null,
                  })} />
              </label>
              {/* Said here because the handle is easy to miss and is the better
                  tool of the two: on imagery where the ring is visible, the
                  picture IS the measurement. */}
              <p className="faint" style={{ margin: 0, fontSize: 12 }}>
                {t('Or drag the small handle on the ring to size it against the picture.')}
              </p>
              {pens.length > 1 && selPen.circumference_m !== null && (
                <div>
                  <button className="btn ghost sm"
                    onClick={() => {
                      touch()
                      setPens((ps) => ps.map((x) =>
                        ({ ...x, circumference_m: selPen.circumference_m })))
                    }}>
                    {t('Give every pen this size')}
                  </button>
                </div>
              )}
              <label className="felt">
                <span>{t('Depth (m)')}</span>
                <input type="number" min={1} max={200} step={1}
                  value={selPen.depth_m ?? ''}
                  onChange={(e) => patch(selPen.key, {
                    depth_m: e.target.value ? Number(e.target.value) : null,
                  })} />
              </label>
              {selPen.shape === 'square' && (
                <label className="felt">
                  <span>{t('Turned')} {Math.round(selPen.bearing_deg ?? 0)}°</span>
                  <input type="range" min={0} max={359} step={1}
                    value={selPen.bearing_deg ?? 0}
                    onChange={(e) => patch(selPen.key, {
                      bearing_deg: Number(e.target.value),
                    })} />
                </label>
              )}
              <div>
                <button className="btn ghost sm"
                  onClick={() => drop(selPen.key)}>
                  {t('Remove this pen')}
                </button>
              </div>
              {/* Said where the button is, because it is the one act on this
                  screen that reaches data somebody has already reported. */}
              {selPen.pen_id && (
                <p className="faint" style={{ margin: 0, fontSize: 12 }}>
                  {t('It is retired, not deleted. Counts already made under it stay where they are.')}
                </p>
              )}
            </div>
          )}

          {selStruct && (
            <div className="stack kartvalg" style={{ gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 14 }}>{t('Feed barge')}</h3>
              <label className="felt">
                <span>{t('Name')}</span>
                <input value={selStruct.name ?? ''} maxLength={200}
                  onChange={(e) => patchS(selStruct.key,
                                          { name: e.target.value || null })} />
              </label>
              <label className="felt">
                <span>{t('Length (m)')}</span>
                <input type="number" min={1} max={500}
                  value={selStruct.length_m ?? ''}
                  onChange={(e) => patchS(selStruct.key, {
                    length_m: e.target.value ? Number(e.target.value) : null,
                  })} />
              </label>
              <label className="felt">
                <span>{t('Width (m)')}</span>
                <input type="number" min={1} max={500}
                  value={selStruct.width_m ?? ''}
                  onChange={(e) => patchS(selStruct.key, {
                    width_m: e.target.value ? Number(e.target.value) : null,
                  })} />
              </label>
              <label className="felt">
                <span>{t('Turned')} {Math.round(selStruct.bearing_deg ?? 0)}°</span>
                <input type="range" min={0} max={359} step={1}
                  value={selStruct.bearing_deg ?? 0}
                  onChange={(e) => patchS(selStruct.key, {
                    bearing_deg: Number(e.target.value),
                  })} />
              </label>
              <div>
                <button className="btn ghost sm"
                  onClick={() => drop(selStruct.key)}>
                  {t('Remove')}
                </button>
              </div>
            </div>
          )}

          {!selPen && !selStruct && pens.length > 0 && (
            <p className="faint" style={{ margin: 0, fontSize: 12 }}>
              {t('Tap a pen to rename or resize it. Drag it to move it.')}
            </p>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                        flexWrap: 'wrap' }}>
            <button className="btn primary" disabled={!dirty || saving}
              onClick={() => void save()}>
              {saving ? t('Saving…') : t('Save the layout')}
            </button>
            {saved && !dirty && (
              <span className="faint" style={{ fontSize: 12 }}>
                {t('Saved.')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
